import React from "react";
import { Alert, Box, CircularProgress, Link, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HPButton from "@components/common/Button";
import type { DatasetMetric } from "@models/Metric";
import type { QuestionDetail } from "@models/Dataset";
import MetricBaseSummaryCell from "@components/features/configuration/metrics/MetricBaseSummaryCell";
import MetricSummaryRow from "@components/features/configuration/metrics/MetricSummaryRow";
import {
  describeMetricSelection,
  formatMetricPopulationLabel,
  getMetricResultTypeLabel,
} from "@utils/metric-source-utils";

interface MetricSourceSummarySectionProps {
  datasetId: string | null;
  datasetMetricName: string | null;
  liveMetric: DatasetMetric | null;
  questionDetail: QuestionDetail | null;
  isRemoved: boolean;
  isUnavailableMean: boolean;
  definitionChanged: boolean;
  /** Line Chart only (Breakout is a Line Chart-only feature) — true when a
   *  shared metric edit removed/renamed the represented result the
   *  researcher had chosen as Breakout's Source Line. Bar Chart never
   *  passes this. */
  breakoutSourceInvalidated?: boolean;
  isResolving: boolean;
  fetchError: boolean;
  onRefresh: () => void;
  onRetryFetch: () => void;
}

/**
 * Read-only Chart Source summary for a Metric-sourced Bar Chart — sits above
 * Configurations in the editor, replacing Focus Selection/Base editing/
 * Category Rollups for this path (all read-only here, per the PRD). Mirrors
 * DatasetMetricSelectedSummary (Scorecard's row builder) but scoped to a
 * chart's single top-level metric reference.
 */
const MetricSourceSummarySection: React.FC<MetricSourceSummarySectionProps> = ({
  datasetId,
  datasetMetricName,
  liveMetric,
  questionDetail,
  isRemoved,
  isUnavailableMean,
  definitionChanged,
  breakoutSourceInvalidated = false,
  isResolving,
  fetchError,
  onRefresh,
  onRetryFetch,
}) => {
  const selection =
    liveMetric?.source ? describeMetricSelection(liveMetric.source, questionDetail) : null;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Chart Source: Metric
      </Typography>

      {isRemoved && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This chart's saved metric ("{datasetMetricName}") is no longer available in Dataset
          Configuration. The chart's reference and usage protection are preserved, but it can't
          be recalculated until the metric is restored.
        </Alert>
      )}

      {!isRemoved && isUnavailableMean && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This metric's definition was changed to a Mean result. Bar Charts require Percentage
          metrics — this chart is unavailable until the metric's definition is Percentage again.
          Its reference and usage protection are preserved.
        </Alert>
      )}

      {!isRemoved && !isUnavailableMean && liveMetric && (
        <>
          {definitionChanged && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <HPButton variant="outlined" size="small" onClick={onRefresh}>
                  Refresh
                </HPButton>
              }
            >
              This metric's definition has changed since this chart was last saved. Refresh to
              recalculate with the current definition, then Save to update the chart.
            </Alert>
          )}

          {breakoutSourceInvalidated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This metric's calculation no longer includes the line selected as Breakout's
              Source Line. Select a new Source Line below before saving.
            </Alert>
          )}

          {fetchError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <HPButton variant="outlined" size="small" onClick={onRetryFetch}>
                  Retry
                </HPButton>
              }
            >
              Failed to load this metric's question details.
            </Alert>
          )}

          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", mb: 2 }}>
            {/* #E3F2FD matches DatasetSelector's selected-row background
                (New Bar Chart's dataset picker), for visual consistency
                with the creation modal's Metric summary rather than the
                generic hp-selected-background token. */}
            <Box sx={{ p: 1.5, backgroundColor: "#E3F2FD" }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {liveMetric.name}
              </Typography>
              {liveMetric.source && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word", display: "block", mt: 0.5 }}
                  >
                    {liveMetric.source.question.questionLabel}
                    {" · "}
                    {getMetricResultTypeLabel(liveMetric.source.resultType)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Metric population: {formatMetricPopulationLabel(liveMetric.population)}
                  </Typography>
                </>
              )}
              {isResolving && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Resolving current definition...
                  </Typography>
                </Box>
              )}
            </Box>

            {liveMetric.source && !isResolving && (
              <Box sx={{ p: 1.5, backgroundColor: "#f7fbfe" }}>
                <Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: 0.75 }}>
                  Saved definition
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <MetricSummaryRow label="Source question">
                    <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      {liveMetric.source.question.questionLabel}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word", display: "block" }}
                    >
                      {liveMetric.source.question.questionTitle}
                    </Typography>
                  </MetricSummaryRow>
                  {selection && (
                    <MetricSummaryRow label="Selection">
                      <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {selection.label}
                      </Typography>
                      {selection.details && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: "normal", wordBreak: "break-word", display: "block" }}
                        >
                          {selection.details}
                        </Typography>
                      )}
                    </MetricSummaryRow>
                  )}
                  <MetricSummaryRow label="Metric population">
                    <Typography variant="body2">
                      {formatMetricPopulationLabel(liveMetric.population)}
                    </Typography>
                  </MetricSummaryRow>
                  <MetricSummaryRow label="Base">
                    {datasetId && (
                      <MetricBaseSummaryCell datasetId={datasetId} base={liveMetric.base} />
                    )}
                  </MetricSummaryRow>
                </Box>
                {datasetId && (
                  <Link
                    href={`/datasets/${datasetId}/config`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    variant="caption"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 1 }}
                  >
                    Edit calculation in Dataset Configuration
                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Link>
                )}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default MetricSourceSummarySection;
